import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Trash2, ChartBar } from 'lucide-react';
import { HeadCircuit } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentState } from '@/context/agent-state.context';
import { ChatMessageList } from '@/components/common/chat/ChatMessageList';
import { ChatInput } from '@/components/common/chat/ChatInput';
import type { Message } from '@/context/agent-state.context';
import { useToast } from '@/context/ToastContext';
import type {
  ModuleKey,
  PortalCreatedAction,
  PortalListAction,
  PortalPublishedAction,
  PortalUpdatedAction,
} from '@erp/rpc';
import { cn } from '@/lib/utils';

const assistantMarkdownStyles = cn(
  'text-sm',
  '[&_p]:mb-2 [&_p]:last:mb-0',
  '[&_a]:text-stone-900 [&_a]:underline [&_a]:font-medium [&_a:hover]:text-stone-600',
  '[&_code]:bg-stone-200 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono',
  '[&_pre]:bg-stone-200 [&_pre]:p-2 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:my-2',
  '[&_ul]:pl-4 [&_ul]:my-2 [&_ul]:list-disc',
  '[&_ol]:pl-4 [&_ol]:my-2 [&_ol]:list-decimal',
  '[&_li]:mb-1',
  '[&_strong]:font-semibold',
  '[&_em]:italic',
  '[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2',
  '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1',
  '[&_blockquote]:border-l-2 [&_blockquote]:border-stone-400 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-stone-600 [&_blockquote]:my-2',
  // Table wrapper for horizontal scroll
  '[&_table]:block [&_table]:overflow-x-auto [&_table]:my-3 [&_table]:text-xs [&_table]:rounded-md [&_table]:border [&_table]:border-stone-300 [&_table]:max-w-full',
  '[&_thead]:bg-stone-200',
  '[&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-stone-700 [&_th]:border-b [&_th]:border-stone-300 [&_th]:whitespace-nowrap',
  '[&_td]:px-3 [&_td]:py-2 [&_td]:border-b [&_td]:border-stone-200 [&_td]:whitespace-nowrap',
  '[&_tr:last-child_td]:border-b-0',
  '[&_tr:hover]:bg-stone-50',
  // Tabular numbers for consistent alignment
  '[&_td]:tabular-nums',
);

/** Filter update payload from AI tool */
export interface FilterUpdate {
  cumulative?: boolean;
  divisionNames?: string[];
  supplierNames?: string[];
  excludeDivisionNames?: string[];
  excludeSupplierNames?: string[];
  visibleLines?: (
    | 'estimates'
    | 'predictions' // Shorthand for all prediction algorithms
    | 'linear-regression'
    | 'ai-forecasting'
    | 'sarima'
    | 'yoy-seasonal'
    | 'reported'
    | 'previousYear'
  )[];
  // primaryLine is for table data selection, uses high-level categories only
  primaryLine?: 'estimates' | 'predictions' | 'reported' | 'previousYear';
  clearAllFilters?: boolean;
}

/** Chart data payload from AI tool */
export interface ChartPayload {
  chart: {
    id: string;
    title: string;
    description: string;
    chartType: 'bar' | 'line' | 'pie';
    data: Array<Record<string, unknown>>;
    xAxisLabel: string;
    yAxisLabel: string;
    color: string;
    secondaryColor?: string;
  };
  offerEmailReport: boolean;
}

/** Comparison data payload from AI tool */
export interface ComparisonPayload {
  comparisonData: {
    currentInvoice: {
      id: string;
      invoiceNumber: string;
      invoiceDate: string;
      subtotal: number;
      taxAmount: number;
      totalAmount: number;
    };
    previousInvoices: Array<{
      id: string;
      invoiceNumber: string;
      invoiceDate: string;
      subtotal: number;
      taxAmount: number;
      totalAmount: number;
    }>;
    supplierName: string;
    currency: string;
    requestedN: number;
    actualN: number;
  };
  chart: {
    id: string;
    title: string;
    description: string;
    chartType: 'bar' | 'line';
    data: Array<Record<string, unknown>>;
    xAxisLabel: string;
    yAxisLabel: string;
    color: string;
    secondaryColor?: string;
  };
  defaultTab?: 'table' | 'chart';
}

/** Breakdown table configuration payload from AI tool */
export interface BreakdownTablePayload {
  tableConfig: {
    primaryXDimension: string;
    secondaryXDimension?: string;
    primaryYDimension: string;
    secondaryYDimension?: string;
    timeFilterPreset?: string;
  };
  label: string;
}

/** Temp report table config from AI tool */
export interface TempReportTableConfig {
  id: string;
  primaryXDimension: string;
  secondaryXDimension?: string;
  primaryYDimension: string;
  secondaryYDimension?: string;
  timeFilterPreset?: string;
  label: string;
}

/** Temp report payload from AI tool */
export interface TempReportPayload {
  title: string;
  tables: TempReportTableConfig[];
}

/** Configured field payload from AI tool */
export interface ConfiguredFieldPayload {
  id: string;
  name: string;
  description: string;
  category: 'customer' | 'dates' | 'line_items' | 'shipping' | 'pricing';
  required: boolean;
  action?: 'create' | 'remove';
}

/** Memory bank note payload from AI tool */
export interface MemoryBankNotePayload {
  content: string;
  entityId: string;
  entityType: string;
  entityLabel: string;
  metadata?: Record<string, unknown>;
}

/** AR Collections resolve payload */
export interface ResolveARIssuesPayload {
  customerIds: string[];
  action: 'resolve_all' | 'resolve_selected';
}

/** Collections outreach payload from AI tool */
export interface CollectionsPayload {
  vendorNames: string[];
  message: string;
}

/** Email rule config for workflow creation */
export interface EmailRuleConfig {
  ruleName: string;
  eventName: string;
  filters: {
    subjectContains?: string;
    senderDomainContains?: string;
    senderEmailContains?: string;
  };
}

/** Save and run workflow on invoices payload */
export interface SaveAndRunWorkflowPayload {
  invoiceCount: number;
}

interface ForegroundAgentChatProps {
  moduleKey: ModuleKey;
  currentPage: string;
  hideFloatingButton?: boolean;
  isGlobal?: boolean;
  /** When true, uses absolute positioning relative to parent instead of fixed positioning */
  isEmbedded?: boolean;
  /** Hidden message sent automatically when chat opens to trigger AI greeting */
  initialMessage?: string;
  onNavigate?: (
    path: string,
    options?: { search?: Record<string, unknown> },
  ) => void;
  onAddNode?: (
    stepType: string,
    stepName?: string,
    options?: { silent?: boolean },
  ) => void;
  onUpdateFilters?: (filters: FilterUpdate) => void;
  onShowChart?: (payload: ChartPayload) => void;
  onShowComparison?: (payload: ComparisonPayload) => void;
  onCreateBreakdownTable?: (payload: BreakdownTablePayload) => void;
  onCreateTempReport?: (payload: TempReportPayload) => void;
  onCreateField?: (field: ConfiguredFieldPayload) => void;
  onRemoveField?: (field: ConfiguredFieldPayload) => void;
  onCreateMemoryBankNote?: (note: MemoryBankNotePayload) => Promise<void>;
  onOpenEmailDraft?: (draftId: string) => void;
  onPortalUpdated?: (portalId: string) => void;
  /** AR Collections: resolve issues for customers needing attention */
  onResolveARIssues?: (payload: ResolveARIssuesPayload) => Promise<void>;
  /** Trigger collection outreach to vendors */
  onTriggerCollections?: (payload: CollectionsPayload) => void;
  /** Create email rule and workflow together */
  onCreateWorkflowWithEmailRule?: (config: EmailRuleConfig) => Promise<void>;
  /** Save workflow and navigate to run on invoices */
  onSaveAndRunWorkflowOnInvoices?: (
    payload: SaveAndRunWorkflowPayload,
  ) => Promise<void>;
}

export function ForegroundAgentChat({
  moduleKey,
  currentPage,
  hideFloatingButton,
  isGlobal,
  isEmbedded = false,
  initialMessage,
  onNavigate,
  onAddNode,
  onUpdateFilters,
  onShowChart,
  onShowComparison,
  onCreateBreakdownTable,
  onCreateTempReport,
  onCreateField,
  onRemoveField,
  onCreateMemoryBankNote,
  onOpenEmailDraft,
  onPortalUpdated,
  onResolveARIssues,
  onTriggerCollections,
  onCreateWorkflowWithEmailRule,
  onSaveAndRunWorkflowOnInvoices,
}: ForegroundAgentChatProps) {
  const { showToast } = useToast();
  const {
    isOpen,
    toggle,
    close,
    hasPageLevelChat,
    registerPageChat,
    unregisterPageChat,
  } = useAgentPanel();
  const [inputValue, setInputValue] = useState('');
  const [voiceTranscription, setVoiceTranscription] = useState<string | null>(
    null,
  );
  const [portalHtmlPreview, setPortalHtmlPreview] = useState<{
    portalId: string;
    html: string;
  } | null>(null);

  const {
    isLoading,
    isLoadingHistory,
    messages,
    sendMessage: sendAgentMessage,
    addLocalMessage,
    streamingText,
    cancel,
    reset,
    activeToolCalls,
    error,
    attachWidgetToLastMessage,
    updateWidgetStatus,
  } = useAgentState();

  const processedToolCallsRef = useRef<Set<string>>(new Set());
  const initialMessageSentRef = useRef(false);
  const messagesRef = useRef<Message[]>(messages);
  // Track the last comparison payload to prevent duplicate modal invocations
  const lastComparisonSignatureRef = useRef<string | null>(null);
  // Track if we've initialized processedToolCallsRef with existing tool calls on mount
  const hasInitializedProcessedRef = useRef(false);
  const [pendingField, setPendingField] =
    useState<ConfiguredFieldPayload | null>(null);

  useEffect(() => {
    messagesRef.current = messages;
    if (messages.length === 0) {
      processedToolCallsRef.current.clear();
      lastComparisonSignatureRef.current = null;
      hasInitializedProcessedRef.current = false;
    }
  }, [messages]);

  useEffect(() => {
    if (
      !hasInitializedProcessedRef.current &&
      activeToolCalls.length > 0 &&
      !isLoading
    ) {
      activeToolCalls
        .filter((tc) => tc.status === 'complete')
        .forEach((tc) => processedToolCallsRef.current.add(tc.toolCallId));
      hasInitializedProcessedRef.current = true;
    }
  }, [activeToolCalls, isLoading]);

  // Staged widgets - detected but not shown until AI finishes loading
  const [stagedField, setStagedField] = useState<ConfiguredFieldPayload | null>(
    null,
  );
  const [stagedNote, setStagedNote] = useState<MemoryBankNotePayload | null>(
    null,
  );
  const [pendingNote, setPendingNote] = useState<MemoryBankNotePayload | null>(
    null,
  );

  // Track previous loading state to detect when loading finishes
  const wasLoadingRef = useRef(false);

  // Context-aware loading messages based on active tool calls
  const getLoadingMessages = useCallback(() => {
    const pendingToolCall = activeToolCalls.find(
      (tc) => tc.status === 'pending',
    );

    if (pendingToolCall) {
      switch (pendingToolCall.toolName) {
        case 'createMemoryBankNote':
          return ['Creating note...', 'Preparing note...', 'Almost ready...'];
        case 'createConfiguredField':
          return [
            'Configuring field...',
            'Checking schema...',
            'Almost ready...',
          ];
        default:
          return ['Processing...', 'Working on it...', 'Almost ready...'];
      }
    }

    return ['Analyzing...', 'Thinking...', 'Preparing response...'];
  }, [activeToolCalls]);

  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const loadingMessages = getLoadingMessages();

  // Rotate loading messages while loading
  useEffect(() => {
    if (!isLoading) {
      setLoadingMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 1500);

    return () => clearInterval(interval);
  }, [isLoading, loadingMessages.length]);

  const loadingStatusMessage = isLoading
    ? loadingMessages[loadingMessageIndex]
    : undefined;

  // When loading finishes, attach staged widgets to the last assistant message
  useEffect(() => {
    if (wasLoadingRef.current && !isLoading) {
      // Loading just finished - attach any staged widgets to last message
      if (stagedField) {
        attachWidgetToLastMessage({
          type: 'field',
          data: {
            name: stagedField.name,
            description: stagedField.description,
          },
        });
        // Store the full payload for the confirm handler
        setPendingField(stagedField);
        setStagedField(null);
      }
      if (stagedNote) {
        attachWidgetToLastMessage({
          type: 'memoryBankNote',
          data: {
            content: stagedNote.content,
            entityId: stagedNote.entityId,
            entityType: stagedNote.entityType,
            entityLabel: stagedNote.entityLabel,
            metadata: stagedNote.metadata,
          },
        });
        // Store the full payload for the confirm handler
        setPendingNote(stagedNote);
        setStagedNote(null);
      }
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading, stagedField, stagedNote, attachWidgetToLastMessage]);

  // Send initial message when chat opens for the first time
  useEffect(() => {
    if (
      initialMessage &&
      isOpen &&
      messages.length === 0 &&
      !isLoading &&
      !initialMessageSentRef.current
    ) {
      initialMessageSentRef.current = true;
      sendAgentMessage(initialMessage, moduleKey, currentPage, {
        hidden: true,
      });
    }
  }, [
    initialMessage,
    isOpen,
    messages.length,
    isLoading,
    sendAgentMessage,
    moduleKey,
    currentPage,
  ]);

  useEffect(() => {
    if (isGlobal) return;
    registerPageChat();
    return () => unregisterPageChat();
  }, [isGlobal, registerPageChat, unregisterPageChat]);

  useEffect(() => {
    if (!onNavigate) return;

    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'navigate',
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'path' in completedToolCall.result
    ) {
      const result = completedToolCall.result as {
        action: string;
        path: string;
        search?: Record<string, unknown>;
      };
      onNavigate(result.path, { search: result.search });
    }
  }, [activeToolCalls, onNavigate]);

  useEffect(() => {
    if (!onAddNode) return;

    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'addNode',
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'stepType' in completedToolCall.result
    ) {
      const result = completedToolCall.result as {
        action: string;
        stepType: string;
        stepName?: string;
      };
      onAddNode(result.stepType, result.stepName, { silent: true });
    }
  }, [activeToolCalls, onAddNode]);

  useEffect(() => {
    if (!onUpdateFilters) return;

    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'updateFilters' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'filters' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as {
        action: string;
        filters: FilterUpdate;
      };
      onUpdateFilters(result.filters);
    }
  }, [activeToolCalls, onUpdateFilters]);

  useEffect(() => {
    if (!onShowChart) return;

    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'showChart' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'chart' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as {
        action: string;
        chart: ChartPayload['chart'];
        offerEmailReport: boolean;
      };
      onShowChart({
        chart: result.chart,
        offerEmailReport: result.offerEmailReport,
      });
    }
  }, [activeToolCalls, onShowChart]);

  // Handle portalCreated action from AI tool
  useEffect(() => {
    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'portalCreated' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'portalId' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as PortalCreatedAction;

      showToast({
        title: 'Portal created',
        message: result.portalName,
        color: 'success',
        icon: 'check',
      });

      if (onNavigate) {
        onNavigate(result.editUrl);
      }
    }
  }, [activeToolCalls, onNavigate, showToast]);

  // Handle portalUpdated action from AI tool
  useEffect(() => {
    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'portalUpdated' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'portalId' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as PortalUpdatedAction;

      onPortalUpdated?.(result.portalId);
      showToast({
        title: 'Portal updated',
        message: 'Preview refreshed.',
        color: 'success',
        icon: 'check',
      });
    }
  }, [activeToolCalls, showToast, onPortalUpdated]);

  // Handle portalPublished action from AI tool
  useEffect(() => {
    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'portalPublished' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'portalUrl' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as PortalPublishedAction;

      showToast({
        title: 'Portal published',
        message: result.portalUrl,
        color: 'success',
        icon: 'check',
      });
    }
  }, [activeToolCalls, showToast]);

  // Handle portalList action from AI tool
  useEffect(() => {
    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'portalList' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'portals' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as PortalListAction;

      showToast({
        title: 'Portals',
        message: `Found ${result.portals.length} portal(s).`,
        color: 'info',
        icon: 'info',
      });
    }
  }, [activeToolCalls, showToast]);

  // Handle showComparison action from AI tool
  useEffect(() => {
    if (!onShowComparison) return;

    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'showComparison' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'comparisonData' in completedToolCall.result &&
      'chart' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as {
        action: string;
        comparisonData: ComparisonPayload['comparisonData'];
        chart: ComparisonPayload['chart'];
        defaultTab?: 'table' | 'chart';
      };

      const payload: ComparisonPayload = {
        comparisonData: result.comparisonData,
        chart: result.chart,
        defaultTab: result.defaultTab,
      };

      // Dedupe by comparison content - prevents re-showing the same comparison
      // when backend re-emits tool calls with new IDs on subsequent messages
      const signature = `${payload.comparisonData.currentInvoice.id}:${payload.comparisonData.previousInvoices.map((inv) => inv.id).join(',')}`;
      if (signature === lastComparisonSignatureRef.current) {
        return;
      }
      lastComparisonSignatureRef.current = signature;

      onShowComparison(payload);
    }
  }, [activeToolCalls, onShowComparison]);

  // Handle createBreakdownTable action from AI tool
  useEffect(() => {
    if (!onCreateBreakdownTable) return;

    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'createBreakdownTable' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'tableConfig' in completedToolCall.result &&
      'label' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as {
        action: string;
        tableConfig: BreakdownTablePayload['tableConfig'];
        label: string;
      };

      onCreateBreakdownTable({
        tableConfig: result.tableConfig,
        label: result.label,
      });
    }
  }, [activeToolCalls, onCreateBreakdownTable]);

  // Handle createTempReport action from AI tool
  useEffect(() => {
    if (!onCreateTempReport) return;

    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'createTempReport' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'reportConfig' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as {
        action: string;
        reportConfig: {
          title: string;
          tables: TempReportTableConfig[];
        };
      };

      onCreateTempReport({
        title: result.reportConfig.title,
        tables: result.reportConfig.tables,
      });
    }
  }, [activeToolCalls, onCreateTempReport]);

  // Detect createField action - stage widget until loading finishes
  useEffect(() => {
    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'createField' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'field' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as {
        action: string;
        field: ConfiguredFieldPayload;
      };
      // Stage the widget - it will be shown when loading finishes
      setStagedField(result.field);
    }
  }, [activeToolCalls]);

  // Detect createMemoryBankNote action - stage widget until loading finishes
  useEffect(() => {
    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'createMemoryBankNote' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'note' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as {
        action: string;
        note: MemoryBankNotePayload;
      };
      // Stage the widget - it will be shown when loading finishes
      setStagedNote(result.note);
    }
  }, [activeToolCalls]);

  const handleConfirmField = useCallback(
    (messageId: string) => {
      if (pendingField) {
        // Call the appropriate callback based on action
        if (pendingField.action === 'remove') {
          if (onRemoveField) {
            onRemoveField(pendingField);
          }
        } else {
          if (onCreateField) {
            onCreateField(pendingField);
          }
        }
        updateWidgetStatus(messageId, 'confirmed');
        setPendingField(null);
      }
    },
    [pendingField, onCreateField, onRemoveField, updateWidgetStatus],
  );

  const handleCancelField = useCallback(
    (messageId: string) => {
      if (pendingField) {
        updateWidgetStatus(messageId, 'rejected');
        setPendingField(null);
      }
    },
    [pendingField, updateWidgetStatus],
  );

  const handleConfirmNote = useCallback(
    async (messageId: string) => {
      if (pendingNote) {
        // Call the callback if provided
        if (onCreateMemoryBankNote) {
          try {
            await onCreateMemoryBankNote(pendingNote);
            updateWidgetStatus(messageId, 'confirmed');
          } catch {
            // Keep as pending if creation fails
            return;
          }
        } else {
          updateWidgetStatus(messageId, 'confirmed');
        }
        setPendingNote(null);
      }
    },
    [pendingNote, onCreateMemoryBankNote, updateWidgetStatus],
  );

  const handleCancelNote = useCallback(
    (messageId: string) => {
      if (pendingNote) {
        updateWidgetStatus(messageId, 'rejected');
        setPendingNote(null);
      }
    },
    [pendingNote, updateWidgetStatus],
  );

  // Handle openEmailDraft action from AI tool
  useEffect(() => {
    if (!onOpenEmailDraft) return;

    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        tc.result.action === 'openEmailDraft' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'draftId' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as {
        action: string;
        draftId: string;
      };

      onOpenEmailDraft(result.draftId);
    }
  }, [activeToolCalls, onOpenEmailDraft]);

  // Handle resolveARIssues action from AI tool
  useEffect(() => {
    if (!onResolveARIssues) return;

    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        (tc.result as { action: string }).action === 'resolveARIssues' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'customerIds' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as {
        action: string;
        customerIds: string[];
        resolveAction: 'resolve_all' | 'resolve_selected';
      };

      onResolveARIssues({
        customerIds: result.customerIds,
        action: result.resolveAction,
      });
    }
  }, [activeToolCalls, onResolveARIssues]);

  // Handle triggerCollections action from AI tool
  useEffect(() => {
    if (!onTriggerCollections) return;

    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        (tc.result as { action: string }).action === 'triggerCollections' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'vendorNames' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as {
        action: string;
        vendorNames: string[];
        message: string;
      };

      onTriggerCollections({
        vendorNames: result.vendorNames,
        message: result.message,
      });
    }
  }, [activeToolCalls, onTriggerCollections]);

  useEffect(() => {
    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        (tc.result as { action: string }).action ===
          'createWorkflowWithEmailRule' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'emailRuleConfig' in completedToolCall.result &&
      'path' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as {
        action: string;
        path: string;
        search?: Record<string, unknown>;
        emailRuleConfig: EmailRuleConfig;
      };

      if (onCreateWorkflowWithEmailRule) {
        onCreateWorkflowWithEmailRule(result.emailRuleConfig)
          .then(() => {
            if (onNavigate) {
              onNavigate(result.path, { search: result.search });
            }
          })
          .catch((err) => {
            console.error(
              'Failed to create email rule, navigating anyway:',
              err,
            );
            if (onNavigate) {
              onNavigate(result.path, { search: result.search });
            }
          });
      } else if (onNavigate) {
        onNavigate(result.path, { search: result.search });
      }
    }
  }, [activeToolCalls, onCreateWorkflowWithEmailRule, onNavigate]);

  useEffect(() => {
    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        (tc.result as { action: string }).action ===
          'saveAndRunWorkflowOnInvoices' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (
      completedToolCall &&
      completedToolCall.result &&
      typeof completedToolCall.result === 'object' &&
      'invoiceCount' in completedToolCall.result &&
      'path' in completedToolCall.result
    ) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      const result = completedToolCall.result as {
        action: string;
        invoiceCount: number;
        path: string;
      };

      if (onSaveAndRunWorkflowOnInvoices) {
        void onSaveAndRunWorkflowOnInvoices({
          invoiceCount: result.invoiceCount,
        }).then(() => {
          if (onNavigate) {
            onNavigate(result.path);
          }
        });
      } else if (onNavigate) {
        onNavigate(result.path);
      }
    }
  }, [activeToolCalls, onSaveAndRunWorkflowOnInvoices, onNavigate]);

  useEffect(() => {
    const completedToolCall = activeToolCalls.find(
      (tc) =>
        tc.status === 'complete' &&
        tc.result &&
        typeof tc.result === 'object' &&
        'action' in tc.result &&
        (tc.result as { action: string }).action === 'refreshPage' &&
        !processedToolCallsRef.current.has(tc.toolCallId),
    );

    if (completedToolCall) {
      processedToolCallsRef.current.add(completedToolCall.toolCallId);

      showToast({
        title: 'Database reseeded',
        message: 'Refreshing page...',
        color: 'success',
        icon: 'check',
      });

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  }, [activeToolCalls, showToast]);

  const detectARCommand = useCallback(
    (message: string): 'resolve_all' | null => {
      if (!onResolveARIssues) return null;

      const lower = message.toLowerCase();
      // Match patterns like "resolve all", "address need attention", "fix all issues", etc.
      const resolvePatterns = [
        /resolve\s*(all|the|every|customers?|issues?|need|attention)/i,
        /address\s*(all|the|every|customers?|issues?|need|attention)/i,
        /fix\s*(all|the|every|customers?|issues?|need|attention)/i,
        /handle\s*(all|the|every|customers?|issues?|need|attention)/i,
        /take\s*care\s*(of)?\s*(all|the|every|customers?|issues?|need|attention)/i,
        /work\s*on\s*(all|the|every|customers?|issues?|need|attention)/i,
        /need\s*attention/i,
      ];

      for (const pattern of resolvePatterns) {
        if (pattern.test(lower)) {
          return 'resolve_all';
        }
      }

      return null;
    },
    [onResolveARIssues],
  );

  const handleSend = async (messageOverride?: string) => {
    const message = messageOverride ?? inputValue;
    const trimmed = message.trim();
    if (!trimmed || isLoading) return;

    // Check for AR-specific commands that should be handled locally
    const arCommand = detectARCommand(trimmed);
    if (arCommand === 'resolve_all' && onResolveARIssues) {
      // Clear input immediately
      if (!messageOverride) {
        setInputValue('');
      }

      // Add user message locally (don't send to backend)
      addLocalMessage('user', trimmed);

      // Add "working on it" response
      setTimeout(() => {
        addLocalMessage(
          'assistant',
          "Got it! I'm resolving all customers that need attention. Moving them to 'In Progress' status and initiating automated follow-up...",
        );
      }, 300);

      // Trigger the action
      setTimeout(async () => {
        await onResolveARIssues({
          customerIds: [],
          action: 'resolve_all',
        });

        // Add completion message
        addLocalMessage(
          'assistant',
          "Done! All customers have been moved to 'In Progress'. I'll be sending follow-up communications and tracking responses for each account.",
        );
      }, 800);

      return;
    }

    // Normal flow - send to backend
    sendAgentMessage(trimmed, moduleKey, currentPage);
    if (!messageOverride) {
      setInputValue('');
    }
  };

  // Render message actions for comparison button
  const renderMessageActions = (msg: Message) => {
    if (msg.auxiliaryContent?.action === 'showComparison') {
      return (
        <div className="mt-2 flex justify-start">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onShowComparison?.(
                msg.auxiliaryContent?.payload as ComparisonPayload,
              )
            }
            className="gap-1.5"
          >
            <ChartBar className="h-4 w-4" />
            View Comparison
          </Button>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {!isOpen && !hideFloatingButton && (
        <Button
          size="icon"
          onClick={() => toggle()}
          className="fixed top-2 right-2 z-[1500] h-9 w-9 rounded-lg bg-stone-700 hover:bg-stone-800 text-white shadow-lg"
          aria-label="Open AI Assistant"
        >
          <HeadCircuit size={22} weight="duotone" />
        </Button>
      )}

      {isOpen && !(isGlobal && hasPageLevelChat) && (
        <div className="fixed top-0 right-0 h-full w-[400px] bg-background border-l border-stone-200 flex flex-col z-[1500] shadow-lg">
          <div className="flex items-center justify-between p-4 border-b border-stone-200">
            <h2 className="text-lg font-semibold text-stone-800">
              AI Assistant
            </h2>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={reset}
                disabled={isLoading || messages.length === 0}
                className="text-stone-500 hover:text-stone-700"
                aria-label="Clear conversation"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={close}
                className="text-stone-500 hover:text-stone-700"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          <ChatMessageList
            messages={messages}
            streamingText={streamingText}
            isLoading={isLoading}
            isLoadingHistory={isLoadingHistory}
            error={error}
            activeToolCalls={activeToolCalls}
            messageMaxWidth="max-w-[80%]"
            voiceTranscription={voiceTranscription}
            loadingStatusMessage={loadingStatusMessage}
            renderMessageActions={renderMessageActions}
            widgetActions={{
              onConfirmField: pendingField ? handleConfirmField : undefined,
              onRejectField: pendingField ? handleCancelField : undefined,
              onConfirmNote: pendingNote ? handleConfirmNote : undefined,
              onRejectNote: pendingNote ? handleCancelNote : undefined,
            }}
          />

          {portalHtmlPreview && moduleKey === 'portal' && (
            <div className="border-t border-stone-200 bg-stone-50 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-stone-700">
                  Portal HTML preview
                </p>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setPortalHtmlPreview(null)}
                  className="h-7 w-7 text-stone-500 hover:text-stone-700"
                  aria-label="Close portal preview"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="h-[240px] rounded-md overflow-hidden border border-stone-200 bg-white">
                <iframe
                  title={`Portal preview ${portalHtmlPreview.portalId}`}
                  sandbox="allow-forms allow-popups"
                  referrerPolicy="no-referrer"
                  className="w-full h-full"
                  srcDoc={portalHtmlPreview.html}
                />
              </div>
            </div>
          )}

          <div className="p-4 border-t border-stone-200">
            <ChatInput
              value={inputValue}
              onChange={setInputValue}
              onSend={handleSend}
              isLoading={isLoading}
              onCancel={cancel}
              enableVoiceInput={true}
              onVoiceTranscription={setVoiceTranscription}
              autoFocus={isOpen}
            />
          </div>
        </div>
      )}
    </>
  );
}
